/**
 * "Did you message Ravi?" (R6): asks what happened after the realtor tapped
 * Message or Call on a follow-up and came back.
 *
 * Message: Sent records the outbound WhatsApp message (and, for a listing
 * match, links the listing to the lead). They replied records that outbound
 * message and then the pasted reply, in that order. Didn't send records
 * nothing.
 *
 * Call: Spoke to them records an outbound call, with the optional note as
 * its text. No answer and Didn't call record nothing.
 *
 * The outbound message or call is dated at the tap, not at the answer: the
 * prompt can come up to two hours later, and a WhatsApp export imported
 * afterwards matches a hand-logged row only within ten minutes of it. The
 * tap is sent as the time elapsed since it, not as a timestamp: the server
 * dates the row from its own clock minus that time, so it lands within
 * network latency of the tap, a phone clock that is fast or slow does not
 * move it, and the server never sees a future date. A tap more than a day
 * old is sent undated, which the server dates now, because it rejects a
 * longer elapsed time. The pasted reply is dated when it is saved.
 *
 * Closing the prompt records nothing. A failed save keeps it open with the
 * pasted text and shows why under the buttons.
 */

import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { useLogContactMessage } from "@/features/realtor/hooks/use-realtor-crm";
import { linkListingToLead } from "@/features/realtor/services/leads-api.service";
import { errorMessage } from "@/features/realtor/utils/error-message";
import { StudioButton } from "@/features/studio/theme";
import { queryKeys } from "@/lib/query-keys";
import { PROMPT_CONTENT_CLASS } from "./follow-up-format";
import type { PendingHandoff } from "./use-pending-handoff";

/** The server rejects a longer body. */
const MAX_BODY = 5000;

/** Mirrors the backend's `MAX_ELAPSED_MS`; the server rejects a larger value. */
const MAX_ELAPSED_MS = 24 * 60 * 60 * 1000;

type Step = "choose" | "reply" | "note";

export function OutcomePrompt({
	handoff,
	onDismiss,
	onRecorded,
	onCloseAutoFocus,
}: {
	handoff: PendingHandoff;
	/** Closed without recording anything. */
	onDismiss: () => void;
	/** A conversation was recorded; offer the next step. */
	onRecorded: (handoff: PendingHandoff) => void;
	/** Where focus goes when the prompt closes. */
	onCloseAutoFocus?: (event: Event) => void;
}) {
	const { t } = useTranslation();
	const ids = useId();
	const logMessage = useLogContactMessage();
	const queryClient = useQueryClient();
	const [step, setStep] = useState<Step>("choose");
	const [text, setText] = useState("");
	const [error, setError] = useState<string | null>(null);
	// "They replied" is two writes. Once the outbound one is saved, a retry
	// after a failed reply sends only the reply.
	const [outboundSaved, setOutboundSaved] = useState(false);
	const [busy, setBusy] = useState(false);
	const isMessage = handoff.action === "message";
	const name = handoff.contactName;

	const log = (
		direction: "outbound" | "inbound",
		channel: "whatsapp" | "call",
		body: string,
	) => {
		const elapsedMs = Math.max(0, Math.round(Date.now() - handoff.createdAt));
		return logMessage.mutateAsync({
			contactId: handoff.contactId,
			input: {
				direction,
				channel,
				body,
				...(direction === "outbound" &&
				Number.isFinite(elapsedMs) &&
				elapsedMs <= MAX_ELAPSED_MS
					? { elapsedMs }
					: {}),
				...(handoff.leadId ? { leadId: handoff.leadId } : {}),
			},
		});
	};

	const record = async (writes: () => Promise<void>) => {
		if (busy) return;
		setBusy(true);
		setError(null);
		try {
			await writes();
			onRecorded(handoff);
		} catch (failure: unknown) {
			setError(errorMessage(failure, t("realtor.followUps.outcome.failed")));
		} finally {
			setBusy(false);
		}
	};

	const saveOutbound = async () => {
		if (outboundSaved) return;
		await log("outbound", "whatsapp", handoff.message);
		setOutboundSaved(true);
	};

	const sent = () =>
		record(async () => {
			await saveOutbound();
			if (handoff.listingId && handoff.leadId) {
				const linked = await linkListingToLead(
					handoff.leadId,
					handoff.listingId,
				);
				if (!linked.success) {
					throw new Error(
						linked.error ?? t("realtor.followUps.outcome.linkFailed"),
					);
				}
				// Logging the message refetched the follow-ups before this
				// link, so the listing match would still show without this.
				await queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.followUps(),
				});
			}
		});

	const theyReplied = () => {
		const reply = text.trim();
		if (!reply) return;
		void record(async () => {
			await saveOutbound();
			await log("inbound", "whatsapp", reply);
		});
	};

	const spokeToThem = () =>
		record(async () => {
			await log(
				"outbound",
				"call",
				text.trim() || t("realtor.followUps.outcome.callBody"),
			);
		});

	const title = isMessage
		? t("realtor.followUps.outcome.messageTitle", { name })
		: t("realtor.followUps.outcome.callTitle", { name });

	return (
		<Dialog
			open
			onOpenChange={(next) => {
				if (!next && !busy) onDismiss();
			}}
		>
			<DialogContent
				className={PROMPT_CONTENT_CLASS}
				onCloseAutoFocus={onCloseAutoFocus}
			>
				<DialogHeader>
					<DialogTitle className="font-display text-xl italic font-medium text-dashboard-text">
						{title}
					</DialogTitle>
					<DialogDescription className="text-sm text-dashboard-muted">
						{t("realtor.followUps.outcome.description")}
					</DialogDescription>
				</DialogHeader>

				{step === "choose" && (
					<div className="flex flex-col gap-2">
						{isMessage ? (
							<>
								<StudioButton disabled={busy} onClick={() => void sent()}>
									{busy && <Loader2 className="h-4 w-4 animate-spin" />}
									{t("realtor.followUps.outcome.sent")}
								</StudioButton>
								<StudioButton
									variant="secondary"
									disabled={busy}
									onClick={() => setStep("reply")}
								>
									{t("realtor.followUps.outcome.theyReplied")}
								</StudioButton>
								<StudioButton
									variant="ghost"
									disabled={busy}
									onClick={onDismiss}
								>
									{t("realtor.followUps.outcome.didntSend")}
								</StudioButton>
							</>
						) : (
							<>
								<StudioButton disabled={busy} onClick={() => setStep("note")}>
									{t("realtor.followUps.outcome.spokeToThem")}
								</StudioButton>
								<StudioButton
									variant="secondary"
									disabled={busy}
									onClick={onDismiss}
								>
									{t("realtor.followUps.outcome.noAnswer")}
								</StudioButton>
								<StudioButton
									variant="ghost"
									disabled={busy}
									onClick={onDismiss}
								>
									{t("realtor.followUps.outcome.didntCall")}
								</StudioButton>
							</>
						)}
					</div>
				)}

				{step !== "choose" && (
					<form
						className="flex flex-col gap-2"
						onSubmit={(event) => {
							event.preventDefault();
							if (step === "reply") theyReplied();
							else void spokeToThem();
						}}
					>
						<label
							htmlFor={`${ids}-text`}
							className="text-[11px] font-semibold uppercase tracking-wider text-dashboard-muted"
						>
							{step === "reply"
								? t("realtor.followUps.outcome.replyLabel", { name })
								: t("realtor.followUps.outcome.noteLabel")}
						</label>
						<textarea
							id={`${ids}-text`}
							autoFocus
							value={text}
							onChange={(event) => setText(event.target.value)}
							maxLength={MAX_BODY}
							rows={4}
							placeholder={
								step === "reply"
									? t("realtor.followUps.outcome.replyPlaceholder")
									: t("realtor.followUps.outcome.notePlaceholder")
							}
							className="w-full resize-y rounded-lg border border-dashboard-border bg-dashboard-paper px-3 py-2 text-sm text-dashboard-text placeholder:text-dashboard-muted focus:outline-none focus:ring-2 focus:ring-dashboard-accent/40"
						/>
						<StudioButton
							type="submit"
							disabled={busy || (step === "reply" && !text.trim())}
						>
							{busy && <Loader2 className="h-4 w-4 animate-spin" />}
							{t("realtor.followUps.outcome.save")}
						</StudioButton>
						<StudioButton
							variant="ghost"
							disabled={busy}
							onClick={() => {
								setStep("choose");
								setError(null);
							}}
						>
							{t("realtor.followUps.outcome.back")}
						</StudioButton>
					</form>
				)}

				{error && (
					<p role="alert" className="text-sm text-dashboard-pill-risk">
						{error}
					</p>
				)}
			</DialogContent>
		</Dialog>
	);
}
